import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  LinearProgress,
  Grid,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { Info as InfoIcon, Edit as EditIcon, Close as CloseIcon } from '@mui/icons-material';

interface Goal {
  id: string;
  name: string;
  target: number;
  current: number;
  description: string;
  dueDate: string;
}

const initialGoals: Goal[] = [
  {
    id: '1',
    name: 'Overall Attendance Rate',
    target: 95,
    current: 92,
    description: 'School-wide attendance target for current semester',
    dueDate: '2023-12-31'
  },
  {
    id: '2',
    name: 'Intervention Success Rate',
    target: 85,
    current: 78,
    description: 'Percentage of successful interventions',
    dueDate: '2023-12-31'
  },
  {
    id: '3',
    name: 'Early Intervention Rate',
    target: 90,
    current: 87,
    description: 'Rate of identifying and addressing issues within first 2 weeks',
    dueDate: '2023-12-31'
  },
  {
    id: '4',
    name: 'Parent Engagement',
    target: 80,
    current: 65,
    description: 'Percentage of parents actively engaged in intervention processes',
    dueDate: '2023-12-31'
  }
];

export const GoalProgress: React.FC = () => {
  const [goals, setGoals] = useState<Goal[]>(initialGoals);
  const [isEditing, setIsEditing] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);

  const getProgressColor = (current: number, target: number) => {
    const progress = (current / target) * 100;
    if (progress >= 90) return 'success';
    if (progress >= 70) return 'warning';
    return 'error';
  };

  const handleEditClick = (goal: Goal) => {
    setEditingGoal(goal);
  };

  const handleSaveGoal = () => {
    if (editingGoal) {
      setGoals(prevGoals =>
        prevGoals.map(goal =>
          goal.id === editingGoal.id ? editingGoal : goal
        )
      );
      setEditingGoal(null);
    }
  };

  const handleInputChange = (field: keyof Goal, value: string | number) => {
    if (editingGoal) {
      setEditingGoal({
        ...editingGoal,
        [field]: field === 'target' || field === 'current' ? Number(value) : value
      });
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Goal Progress</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<EditIcon />}
          onClick={() => setIsEditing(!isEditing)}
          color={isEditing ? 'secondary' : 'primary'}
        >
          {isEditing ? 'Done Editing' : 'Edit Goals'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {goals.map((goal) => (
          <Grid item xs={12} md={6} key={goal.id}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>
                  {goal.name}
                </Typography>
                <Tooltip title={goal.description}>
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {isEditing && (
                  <IconButton 
                    size="small" 
                    onClick={() => handleEditClick(goal)}
                    sx={{ ml: 'auto' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ flexGrow: 1, mr: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(goal.current / goal.target) * 100}
                    color={getProgressColor(goal.current, goal.target)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, textAlign: 'right' }}>
                  {goal.current}%/{goal.target}%
                </Typography>
              </Box>

              <Typography variant="caption" color="text.secondary">
                Due: {new Date(goal.dueDate).toLocaleDateString()}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Dialog 
        open={Boolean(editingGoal)} 
        onClose={() => setEditingGoal(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Edit Goal
            <IconButton size="small" onClick={() => setEditingGoal(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {editingGoal && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Goal Name"
                value={editingGoal.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                fullWidth
              />
              <TextField
                label="Description"
                value={editingGoal.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={2}
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Current Progress"
                  type="number"
                  value={editingGoal.current}
                  onChange={(e) => handleInputChange('current', e.target.value)}
                  inputProps={{ min: 0, max: 100 }}
                  fullWidth
                />
                <TextField
                  label="Target"
                  type="number"
                  value={editingGoal.target}
                  onChange={(e) => handleInputChange('target', e.target.value)}
                  inputProps={{ min: 0, max: 100 }}
                  fullWidth
                />
              </Box>
              <TextField
                label="Due Date"
                type="date"
                value={editingGoal.dueDate}
                onChange={(e) => handleInputChange('dueDate', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingGoal(null)}>Cancel</Button>
          <Button onClick={handleSaveGoal} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};